import { useState, useCallback, useRef } from 'react';
import { useApiService } from '@/lib/api';

export interface ChatSummary {
  _id: string;
  title: string;
  isBeacon: boolean;
  isFavorite: boolean;
  isDeleted: boolean;
  createdAt: string;
  subjectId?: string;
  entityId?: string;
  // Note: We don't cache history content - always fetch fresh from specific chat API
}

export interface ConversationResponse {
  chats: ChatSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ConversationCache {
  conversations: ChatSummary[];
  hasLoaded: boolean;
  currentPage: number;
  hasMore: boolean;
  lastFetchTime: number;
}

// Global cache that persists across component re-mounts
let globalConversationCache: ConversationCache = {
  conversations: [],
  hasLoaded: false,
  currentPage: 1,
  hasMore: false,
  lastFetchTime: 0
};

// Make it globally accessible
(globalThis as any).globalConversationCache = globalConversationCache;

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export const useConversationCache = () => {
  const [cache, setCache] = useState<ConversationCache>(globalConversationCache);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiService = useApiService();
  const fetchPromiseRef = useRef<Promise<ConversationResponse> | null>(null);

  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    const now = Date.now();
    return globalConversationCache.hasLoaded && 
           (now - globalConversationCache.lastFetchTime) < CACHE_DURATION;
  }, []);

  // Update global cache and local state
  const updateCache = useCallback((newCache: Partial<ConversationCache>) => {
    globalConversationCache = { ...globalConversationCache, ...newCache };
    (globalThis as any).globalConversationCache = globalConversationCache;
    setCache(globalConversationCache);
  }, []);

  // Add new conversation to cache (called when new chat is created)
  const addConversation = useCallback((newChat: ChatSummary) => {
    const updatedConversations = [newChat, ...globalConversationCache.conversations];
    updateCache({
      conversations: updatedConversations,
      lastFetchTime: Date.now()
    });
  }, [updateCache]);

  // Force refresh conversations (called after new interaction)
  const refreshConversations = useCallback(async () => {
    // Clear cache and reset promise to force fresh fetch
    fetchPromiseRef.current = null;
    updateCache({
      conversations: [],
      hasLoaded: false,
      currentPage: 1,
      hasMore: false,
      lastFetchTime: 0
    });
    
    try {
      // Force fresh fetch by bypassing cache check
      const result = await fetchConversations(1, false, true);
      return result;
    } catch (error) {
      console.error('❌ refreshConversations: Failed to refresh conversations:', error);
      throw error;
    }
  }, [updateCache]);

  const fetchConversations = useCallback(async (page: number = 1, append: boolean = false, force: boolean = false) => {
    // Return cached data if valid and not appending and not forcing
    if (!append && !force && isCacheValid()) {
      return globalConversationCache;
    }

    // Prevent duplicate API calls
    if (fetchPromiseRef.current && !force) {
      return await fetchPromiseRef.current;
    }

    try {
      if (!append) {
        setIsLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const fetchPromise = apiService.get<ConversationResponse>(
        `/chats/conversations?page=${page}&limit=10`
      );
      
      fetchPromiseRef.current = fetchPromise;
      const response = await fetchPromise;

      if (response.chats) {
        const newConversations = append 
          ? [...globalConversationCache.conversations, ...response.chats]
          : response.chats;

        updateCache({
          conversations: newConversations,
          hasLoaded: true,
          currentPage: page,
          hasMore: page < response.pagination.totalPages,
          lastFetchTime: Date.now()
        });
      }

      return response;
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversation history');
      throw err;
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
      fetchPromiseRef.current = null;
    }
  }, [apiService, isCacheValid, updateCache]);

  const updateConversation = useCallback((chatId: string, updates: Partial<ChatSummary>) => {
    const updatedConversations = globalConversationCache.conversations.map(chat => 
      chat._id === chatId ? { ...chat, ...updates } : chat
    );
    
    updateCache({ 
      conversations: updatedConversations,
      lastFetchTime: Date.now()
    });
  }, [updateCache]);

  const removeConversation = useCallback((chatId: string) => {
    const filteredConversations = globalConversationCache.conversations.filter(
      chat => chat._id !== chatId
    );
    
    updateCache({ 
      conversations: filteredConversations,
      lastFetchTime: Date.now()
    });
  }, [updateCache]);

  const invalidateCache = useCallback(() => {
    updateCache({
      conversations: [],
      hasLoaded: false,
      currentPage: 1,
      hasMore: false,
      lastFetchTime: 0
    });
  }, [updateCache]);

  return {
    conversations: cache.conversations,
    hasLoaded: cache.hasLoaded,
    currentPage: cache.currentPage,
    hasMore: cache.hasMore,
    isLoading,
    loadingMore,
    error,
    fetchConversations,
    addConversation,
    updateConversation,
    removeConversation,
    refreshConversations,
    invalidateCache,
    isCacheValid: isCacheValid()
  };
};
